import { api } from './api';

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Register the service worker (idempotent). Called once on app boot. */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/sw.js');
  } catch {
    /* SW registration failures are non-fatal — push just won't be available */
  }
}

export function currentPushPermission(): PushPermission {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushPermission;
}

/** Is this browser currently subscribed to push on the server side? */
export async function hasActiveSubscription(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Request permission, subscribe via the browser PushManager using the server's VAPID public
 * key, and register the subscription with the API. Returns the resulting permission state.
 */
export async function enableBrowserNotifications(): Promise<PushPermission> {
  if (!pushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushPermission;

  const reg = await navigator.serviceWorker.ready;

  const res = await api.get<{ data: { publicKey: string | null } }>('/push/vapid-public-key');
  const publicKey = res.data.data.publicKey;
  if (!publicKey) return 'granted'; // server has no VAPID configured yet — nothing to subscribe to

  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  }));

  const json = sub.toJSON();
  await api.post('/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  return 'granted';
}

/**
 * N6 — the server sends to a device only while the login session that registered it is alive, so
 * after a new sign-in this browser's existing subscription must be re-bound to the new session.
 * Called once per signed-in session (WebSocketProvider). Silent and best-effort: it never prompts
 * (permission must already be granted) and never creates a subscription the user didn't enable.
 */
export async function resyncPushSubscription(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const json = sub.toJSON();
    await api.post('/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  } catch {
    /* best-effort */
  }
}

export async function disableBrowserNotifications(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
      await sub.unsubscribe().catch(() => undefined);
    }
  } catch {
    /* best-effort */
  }
}
